// ABOUTME: Selects a company product and synchronizes it with a Formik field.
// ABOUTME: Supports debounced product search, initial selection, and pagination.
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  GetProductsByCompanyQuery,
  GetProductsByCompanyQueryVariables,
  useGetProductsByCompanyQuery,
} from "../../../../../generated/graphql";
import { AsyncSelect } from "../../async-select/AsyncSelect";
import { AsyncSelectData } from "../../async-select/AsyncSelectData";
import { useTranslation } from "react-i18next";
import AuthContext from "src/app/contexts/auth-context";

interface FormikFormSelectProps {
  fieldName: string;
  labelName?: string;
  intialId?: number;
  initialId?: number;
  formik: {
    setFieldValue: (fieldName: string, value: number) => void;
    errors: Record<string, unknown>;
    touched: Record<string, unknown>;
  };
  showLabel?: boolean;
}

type ProductNode = NonNullable<
  NonNullable<GetProductsByCompanyQuery["products"]>["nodes"]
>[number];

const mergeProductNodes = (
  previousNodes: readonly ProductNode[],
  nextNodes: readonly ProductNode[]
): ProductNode[] => {
  const mergedNodes = [...previousNodes];

  nextNodes.forEach((node) => {
    if (!mergedNodes.some((item) => item.id === node.id)) {
      mergedNodes.push(node);
    }
  });

  return mergedNodes;
};

const FormikProductFormSelect = (props: FormikFormSelectProps) => {
  const userService = useContext(AuthContext);
  const [searchInput, setSearchInput] = useState<string>("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState<string>("");
  const [allProducts, setAllProducts] = useState<ProductNode[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const [initialProduct, setInitialProduct] = useState<AsyncSelectData>();
  const hasAppliedInitialSelection = useRef(false);

  const initialProductId = props.initialId ?? props.intialId;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchInput(searchInput.trim());
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  const countryId = Number(
    userService.authContextData?.currentUser?.location?.company?.countryId
  );
  const companyId = Number(
    userService.authContextData?.currentUser?.location?.companyId ||
      userService.authContextData?.currentUser?.location?.company?.id
  );
  const hasValidContext = Number.isFinite(countryId) && Number.isFinite(companyId);

  const productQueryVariables: GetProductsByCompanyQueryVariables = useMemo(
    () => ({
      countryId,
      companyId,
      take: 15,
      cursor: undefined,
      search: debouncedSearchInput,
    }),
    [countryId, companyId, debouncedSearchInput]
  );

  const {
    data: productData,
    loading: loadingProduct,
    fetchMore,
  } = useGetProductsByCompanyQuery({
    variables: productQueryVariables,
    skip: !hasValidContext,
    notifyOnNetworkStatusChange: true,
  });
  const formikSetFieldValue = props.formik.setFieldValue;
  const { t } = useTranslation();

  useEffect(() => {
    if (productData?.products) {
      setAllProducts(productData.products.nodes || []);
      setHasMore(productData.products.pageInfo?.hasNextPage || false);
      setCursor(productData.products.pageInfo?.endCursor || null);
      return;
    }

    setAllProducts([]);
    setHasMore(false);
    setCursor(null);
  }, [productData]);

  useEffect(() => {
    hasAppliedInitialSelection.current = false;
  }, [initialProductId, props.fieldName]);

  useEffect(() => {
    if (initialProductId == null || hasAppliedInitialSelection.current) {
      return;
    }

    const product = allProducts.find((i) => i.id === initialProductId);
    if (!product) {
      return;
    }

    setInitialProduct(
      new AsyncSelectData({
        title: product.title,
        value: product.id,
      })
    );
    formikSetFieldValue(props.fieldName, product.id ?? 0);
    hasAppliedInitialSelection.current = true;
  }, [
    initialProductId,
    allProducts,
    formikSetFieldValue,
    props.fieldName,
  ]);

  const selectedProductChanged = useCallback(
    (data: AsyncSelectData) => {
      if (data?.value == null) {
        return;
      }

      formikSetFieldValue(props.fieldName, Number(data.value));
    },
    [formikSetFieldValue, props.fieldName]
  );

  const handleSearchInput = useCallback((_event: any, value: string) => {
    setSearchInput(value);
  }, []);

  const productOptions = useMemo(
    () =>
      allProducts.map(
        (i) =>
          new AsyncSelectData({
            title: i.title,
            value: i.id,
          })
      ),
    [allProducts]
  );

  const handleLoadMore = useCallback(async () => {
    if (!hasValidContext || !hasMore || !cursor || loadingProduct || loadingMoreProducts) {
      return;
    }

    const loadMoreVariables: GetProductsByCompanyQueryVariables = {
      countryId,
      companyId,
      take: 15,
      cursor,
      search: debouncedSearchInput,
    };

    try {
      setLoadingMoreProducts(true);
      await fetchMore({
        variables: loadMoreVariables,
        updateQuery: (previousQueryResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.products) {
            return previousQueryResult;
          }

          const previousNodes = previousQueryResult.products?.nodes || [];
          const nextNodes = fetchMoreResult.products.nodes || [];
          const mergedNodes = mergeProductNodes(previousNodes, nextNodes);

          return {
            ...previousQueryResult,
            products: {
              ...fetchMoreResult.products,
              nodes: mergedNodes,
            },
          };
        },
      });
    } finally {
      setLoadingMoreProducts(false);
    }
  }, [
    hasValidContext,
    hasMore,
    cursor,
    loadingProduct,
    loadingMoreProducts,
    countryId,
    companyId,
    debouncedSearchInput,
    fetchMore,
  ]);

  return (
    <div className={`mt-3`}>
      <AsyncSelect
        label={
          props.showLabel
            ? props.labelName || t("modules.formik.product")
            : ""
        }
        onItemSelected={selectedProductChanged}
        inputValueChanged={handleSearchInput}
        onLoadMore={handleLoadMore}
        loadingMoreData={loadingMoreProducts}
        defaultSelectOption={initialProduct}
        data={productOptions}
        loadingData={loadingProduct}
        hasError={Boolean(
          props.formik.errors[props.fieldName] &&
            props.formik.touched[props.fieldName]
        )}
        className={`w-full`}
      />
    </div>
  );
};

export { FormikProductFormSelect };
